import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PeopleIcon from "@mui/icons-material/People";
import DomainIcon from "@mui/icons-material/Domain";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { supabase } from "../../lib/supabase";
import {
  paths,
  contactDetailPath,
  organizationDetailPath,
  dealDetailPath,
} from "../../routes/paths";

// SPEC manna-web-ui M-Q1 — global ⌘K palette. Cross-entity quick search +
// page navigation. Results are grouped (Pages, Contacts, Organizations,
// Deals); typing filters live with a 200ms debounce. Visibility is RLS-
// gated — users only see entities they could already see in the lists,
// because the palette reads through the same security_invoker views and
// the same `manna.deals` table the list pages use.

const STATIC_PAGES: { label: string; path: string }[] = [
  { label: "Dashboard", path: paths.dashboard },
  { label: "Contacts", path: paths.contacts },
  { label: "Organizations", path: paths.organizations },
  { label: "Deals", path: paths.deals },
  { label: "Activities", path: paths.activities },
  { label: "Prospects", path: paths.prospects },
  { label: "Triage", path: paths.triage },
  { label: "Partners", path: paths.partners },
  { label: "Marketing — Campaigns", path: paths.marketingCampaigns },
  { label: "Marketing — Landing Pages", path: paths.marketingLandingPages },
  { label: "Agents", path: paths.agents },
  { label: "Runs", path: paths.runs },
  { label: "Approvals", path: paths.approvals },
];

interface ResultItem {
  key: string;
  label: string;
  sub?: string;
  group: "Pages" | "Contacts" | "Organizations" | "Deals";
  onSelect: () => void;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export function CommandPalette({ open, onClose }: Props) {
  // Wrapper exists only to remount the body on each open; that resets all
  // state cleanly without a setState-in-effect pattern.
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { mt: 8, alignSelf: "flex-start" } }}
    >
      {open && <CommandPaletteBody onClose={onClose} />}
    </Dialog>
  );
}

function CommandPaletteBody({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const debouncedQuery = useDebounced(query.trim(), 200);

  const personsQuery = useQuery({
    queryKey: ["palette", "persons", debouncedQuery],
    enabled: debouncedQuery.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      const term = debouncedQuery.replace(/[%,]/g, "");
      const { data, error } = await supabase
        .from("manna_persons_view")
        .select("id, full_name, first_name, last_name, primary_email")
        .or(`full_name.ilike.%${term}%,primary_email.ilike.%${term}%`)
        .limit(5);
      if (error) throw error;
      return data ?? [];
    },
  });

  const orgsQuery = useQuery({
    queryKey: ["palette", "organizations", debouncedQuery],
    enabled: debouncedQuery.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      const term = debouncedQuery.replace(/[%,]/g, "");
      const { data, error } = await supabase
        .from("manna_organizations_view")
        .select("id, name, domain")
        .or(`name.ilike.%${term}%,domain.ilike.%${term}%`)
        .limit(5);
      if (error) throw error;
      return data ?? [];
    },
  });

  const dealsQuery = useQuery({
    queryKey: ["palette", "deals", debouncedQuery],
    enabled: debouncedQuery.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      const term = debouncedQuery.replace(/[%,]/g, "");
      const { data, error } = await supabase
        .schema("manna")
        .from("deals")
        .select("id, name")
        .ilike("name", `%${term}%`)
        .is("deleted_at", null)
        .limit(5);
      if (error) throw error;
      return data ?? [];
    },
  });

  const results: ResultItem[] = useMemo(() => {
    const items: ResultItem[] = [];
    const q = debouncedQuery.toLowerCase();

    const matchedPages = q
      ? STATIC_PAGES.filter((p) => p.label.toLowerCase().includes(q))
      : STATIC_PAGES;
    for (const page of matchedPages.slice(0, 6)) {
      items.push({
        key: `page:${page.path}`,
        label: page.label,
        group: "Pages",
        onSelect: () => {
          navigate(page.path);
          onClose();
        },
      });
    }

    if (q) {
      for (const p of personsQuery.data ?? []) {
        const label =
          (p.full_name as string) || `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim();
        items.push({
          key: `person:${p.id}`,
          label: label || (p.primary_email as string) || "(unnamed)",
          sub: (p.primary_email as string) ?? undefined,
          group: "Contacts",
          onSelect: () => {
            navigate(contactDetailPath(p.id as string));
            onClose();
          },
        });
      }
      for (const o of orgsQuery.data ?? []) {
        items.push({
          key: `org:${o.id}`,
          label: (o.name as string) ?? "(unnamed)",
          sub: (o.domain as string) ?? undefined,
          group: "Organizations",
          onSelect: () => {
            navigate(organizationDetailPath(o.id as string));
            onClose();
          },
        });
      }
      for (const d of dealsQuery.data ?? []) {
        items.push({
          key: `deal:${d.id}`,
          label: (d.name as string) ?? "(unnamed)",
          group: "Deals",
          onSelect: () => {
            navigate(dealDetailPath(d.id as string));
            onClose();
          },
        });
      }
    }

    return items;
  }, [debouncedQuery, personsQuery.data, orgsQuery.data, dealsQuery.data, navigate, onClose]);

  const safeActiveIndex = activeIndex < results.length ? activeIndex : 0;

  const groups = useMemo(() => {
    const order: ResultItem["group"][] = ["Pages", "Contacts", "Organizations", "Deals"];
    return order
      .map((g) => ({ group: g, items: results.filter((r) => r.group === g) }))
      .filter((g) => g.items.length > 0);
  }, [results]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = results[safeActiveIndex];
      if (item) item.onSelect();
    }
  }

  return (
    <DialogContent sx={{ p: 0 }} onKeyDown={handleKeyDown}>
      <TextField
        autoFocus
        fullWidth
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActiveIndex(0);
        }}
        placeholder="Search contacts, organizations, deals, or jump to a page…"
        inputProps={{ "aria-label": "Search Manna" }}
        variant="standard"
        sx={{ p: 2, "& .MuiInput-root": { fontSize: 16 } }}
        InputProps={{
          disableUnderline: true,
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <Divider />
      {results.length === 0 ? (
        <Box sx={{ px: 3, py: 4, textAlign: "center", color: "text.secondary" }}>
          <Typography variant="body2">No matches. Try a different search.</Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
          {groups.map((g) => (
            <Box key={g.group}>
              <Typography
                sx={{
                  px: 2,
                  pt: 1.5,
                  pb: 0.5,
                  fontSize: 10,
                  letterSpacing: 1.4,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontWeight: 700,
                }}
              >
                {g.group}
              </Typography>
              <List disablePadding dense>
                {g.items.map((item) => {
                  const overallIndex = results.indexOf(item);
                  const selected = overallIndex === safeActiveIndex;
                  return (
                    <ListItemButton
                      key={item.key}
                      selected={selected}
                      onMouseEnter={() => setActiveIndex(overallIndex)}
                      onClick={item.onSelect}
                      sx={{ px: 2, py: 0.75 }}
                      data-testid={`palette-item-${item.key}`}
                    >
                      <Box sx={{ mr: 1.25, display: "flex", color: "text.secondary" }}>
                        {item.group === "Contacts" ? (
                          <PeopleIcon fontSize="small" />
                        ) : item.group === "Organizations" ? (
                          <DomainIcon fontSize="small" />
                        ) : item.group === "Deals" ? (
                          <LocalAtmIcon fontSize="small" />
                        ) : (
                          <ArrowOutwardIcon fontSize="small" />
                        )}
                      </Box>
                      <ListItemText
                        primary={item.label}
                        secondary={item.sub}
                        primaryTypographyProps={{ variant: "body2" }}
                        secondaryTypographyProps={{ variant: "caption" }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>
      )}
    </DialogContent>
  );
}
